import {
	closestCenter,
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
	Alert,
	Box,
	Button,
	ButtonBase,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Link,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	Assignment,
	createTodoItem,
	deleteTodoItem,
	getAssignmentById,
	getTodoItems,
	patchAssignmentById,
	reorderTodoItems,
	TodoItem,
	updateTodoItem,
} from "../../api";
import { formatDateTime } from "../../utils/date-formatting";
import AssignmentViewModal from "../assignmentsv2/AssignmentViewModal";
import { PersonModal } from "../boardv2/person-modal";

const DetailRow = ({ label, value }: { label: string; value: string }) => (
	<Box sx={{ display: "flex", gap: 1 }} data-component="todo-detail-row">
		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ minWidth: 96, flexShrink: 0, pt: "2px" }}
		>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ wordBreak: "break-word" }}>
			{value}
		</Typography>
	</Box>
);

const SortableTodoRow = ({
	todo,
	onToggle,
	onDelete,
	onEdit,
	onOpenPerson,
	onOpenAssignment,
}: {
	todo: TodoItem;
	onToggle: (todo: TodoItem) => void;
	onDelete: (id: number) => void;
	onEdit: (id: number, text: string) => void;
	onOpenPerson: (personId: number) => void;
	onOpenAssignment: (assignmentId: number) => void;
}) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: todo.id });
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(todo.text);

	const startEdit = () => {
		setDraft(todo.text);
		setEditing(true);
	};
	const commitEdit = () => {
		setEditing(false);
		onEdit(todo.id, draft);
	};
	const cancelEdit = () => {
		setDraft(todo.text);
		setEditing(false);
	};
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.6 : 1,
	};

	return (
		<Box
			ref={setNodeRef}
			style={style}
			data-component="personal-todo-row"
			sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}
		>
			<Box
				{...listeners}
				{...attributes}
				sx={{ cursor: "grab", display: "flex" }}
				data-component="todo-drag-handle"
			>
				<DragIndicatorIcon fontSize="small" color="action" />
			</Box>
			<Checkbox
				checked={todo.done}
				onChange={() => onToggle(todo)}
				size="small"
				data-component="todo-done-checkbox"
			/>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				{editing ? (
					<TextField
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						onBlur={commitEdit}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								commitEdit();
							} else if (e.key === "Escape") {
								cancelEdit();
							}
						}}
						autoFocus
						fullWidth
						size="small"
						variant="standard"
						data-component="todo-text-edit"
					/>
				) : (
					<ButtonBase
						onClick={startEdit}
						disableRipple
						sx={{
							display: "block",
							width: "100%",
							textAlign: "left",
							cursor: "text",
						}}
						data-component="todo-text-button"
					>
						<Typography
							component="span"
							variant="body2"
							sx={{
								textDecoration: todo.done ? "line-through" : "none",
								color: todo.done ? "text.disabled" : "text.primary",
								wordBreak: "break-word",
							}}
							data-component="todo-text"
						>
							{todo.text}
						</Typography>
					</ButtonBase>
				)}
				{(todo.person_name || todo.assignment_name) && (
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						sx={{ mt: 0.25 }}
						data-component="todo-links"
					>
						{todo.person && todo.person_name && (
							<Link
								component="button"
								type="button"
								onClick={() => onOpenPerson(todo.person!)}
								variant="caption"
								data-component="todo-person-link"
							>
								{todo.person_name}
							</Link>
						)}
						{todo.assignment && todo.assignment_name && (
							<Link
								component="button"
								type="button"
								onClick={() => onOpenAssignment(todo.assignment!)}
								variant="caption"
								color="text.secondary"
								data-component="todo-assignment-link"
							>
								· {todo.assignment_name}
							</Link>
						)}
					</Stack>
				)}
			</Box>
			<Tooltip title="Delete task">
				<IconButton
					size="small"
					onClick={() => onDelete(todo.id)}
					data-component="todo-delete-button"
				>
					<DeleteIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);
};

export const PersonalTodos = () => {
	const [todos, setTodos] = useState<TodoItem[]>([]);
	const [newText, setNewText] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [modalPersonId, setModalPersonId] = useState<number | null>(null);
	const [modalAssignment, setModalAssignment] = useState<Assignment | null>(null);
	const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
	);

	useEffect(() => {
		let active = true;
		getTodoItems()
			.then((items) => {
				if (active) {
					setTodos(items);
					setLoading(false);
				}
			})
			.catch((e: Error) => {
				if (active) {
					setError(e.message);
					setLoading(false);
				}
			});
		return () => {
			active = false;
		};
	}, []);

	const handleAdd = () => {
		const text = newText.trim();
		if (!text) return;
		createTodoItem({ text })
			.then((created) => {
				setTodos((prev) => [...prev, created]);
				setNewText("");
			})
			.catch((e: Error) => setError(e.message));
	};

	const handleToggle = (todo: TodoItem) => {
		updateTodoItem(todo.id, { done: !todo.done })
			.then((updated) => {
				setTodos((prev) =>
					prev.map((t) => (t.id === updated.id ? updated : t))
				);
			})
			.catch((e: Error) => setError(e.message));
	};

	const handleEditText = (id: number, text: string) => {
		const trimmed = text.trim();
		const current = todos.find((t) => t.id === id);
		if (!current || !trimmed || trimmed === current.text) return;
		updateTodoItem(id, { text: trimmed })
			.then((updated) =>
				setTodos((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
			)
			.catch((e: Error) => setError(e.message));
	};

	const handleConfirmDelete = () => {
		if (pendingDeleteId === null) return;
		const id = pendingDeleteId;
		setPendingDeleteId(null);
		deleteTodoItem(id)
			.then(() => setTodos((prev) => prev.filter((t) => t.id !== id)))
			.catch((e: Error) => setError(e.message));
	};

	const handleOpenAssignment = (assignmentId: number) => {
		getAssignmentById(assignmentId)
			.then((assignment) => setModalAssignment(assignment))
			.catch((e: Error) => setError(e.message));
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = todos.findIndex((t) => t.id === active.id);
		const newIndex = todos.findIndex((t) => t.id === over.id);
		if (oldIndex === -1 || newIndex === -1) return;
		const reordered = arrayMove(todos, oldIndex, newIndex);
		setTodos(reordered);
		reorderTodoItems(reordered.map((t) => t.id)).catch((e: Error) =>
			setError(e.message)
		);
	};

	const pendingTodo = todos.find((t) => t.id === pendingDeleteId) ?? null;

	return (
		<Grid size={{ xs: 12, lg: 12 }}>
			<Paper sx={{ p: 2 }} data-component="personal-todos">
				<Typography
					variant="h6"
					component="h6"
					sx={{ mb: 1 }}
					data-component="personal-todos-title"
				>
					My tasks
				</Typography>
				<Stack
					direction="row"
					spacing={1}
					sx={{ mb: 1 }}
					data-component="personal-todo-add"
				>
					<TextField
						value={newText}
						onChange={(e) => setNewText(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleAdd();
						}}
						placeholder="Add a personal task…"
						size="small"
						fullWidth
						data-component="todo-add-input"
					/>
					<IconButton
						onClick={handleAdd}
						color="primary"
						disabled={!newText.trim()}
						data-component="todo-add-button"
					>
						<AddIcon />
					</IconButton>
				</Stack>

				{error && (
					<Alert
						severity="error"
						onClose={() => setError(null)}
						sx={{ mb: 1 }}
						data-component="personal-todos-error"
					>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box
						sx={{ display: "flex", justifyContent: "center", py: 2 }}
						data-component="personal-todos-loading"
					>
						<CircularProgress size={24} />
					</Box>
				) : todos.length === 0 ? (
					<Typography
						variant="body2"
						color="text.secondary"
						data-component="personal-todos-empty"
					>
						No tasks yet.
					</Typography>
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={todos.map((t) => t.id)}
							strategy={verticalListSortingStrategy}
						>
							{todos.map((todo) => (
								<SortableTodoRow
									key={todo.id}
									todo={todo}
									onToggle={handleToggle}
									onDelete={setPendingDeleteId}
									onEdit={handleEditText}
									onOpenPerson={setModalPersonId}
									onOpenAssignment={handleOpenAssignment}
								/>
							))}
						</SortableContext>
					</DndContext>
				)}

				<Dialog
					open={pendingDeleteId !== null}
					onClose={() => setPendingDeleteId(null)}
					data-component="todo-delete-confirm"
				>
					<DialogTitle>Delete this task?</DialogTitle>
					<DialogContent dividers>
						{pendingTodo && (
							<Stack spacing={1} data-component="todo-delete-details">
								<DetailRow label="Note" value={pendingTodo.text} />
								<DetailRow
									label="Status"
									value={pendingTodo.done ? "Done" : "Open"}
								/>
								<DetailRow
									label="Candidate"
									value={pendingTodo.person_name ?? "—"}
								/>
								<DetailRow
									label="Assignment"
									value={pendingTodo.assignment_name ?? "—"}
								/>
								<DetailRow
									label="Created"
									value={formatDateTime(pendingTodo.created)}
								/>
								<DetailRow
									label="Updated"
									value={formatDateTime(pendingTodo.updated)}
								/>
							</Stack>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => setPendingDeleteId(null)}
							data-component="todo-delete-cancel"
						>
							Cancel
						</Button>
						<Button
							color="error"
							variant="contained"
							onClick={handleConfirmDelete}
							data-component="todo-delete-confirm-button"
						>
							Delete
						</Button>
					</DialogActions>
				</Dialog>

				{modalPersonId !== null && (
					<PersonModal
						open={modalPersonId !== null}
						personId={modalPersonId}
						onClose={() => setModalPersonId(null)}
					/>
				)}
				{modalAssignment !== null && (
					<AssignmentViewModal
						id="personal-todo-assignment"
						open={modalAssignment !== null}
						assignment={modalAssignment}
						assignments={[modalAssignment]}
						onSave={(a) =>
							patchAssignmentById(a.id, a).then((res) => {
								setModalAssignment(res);
								return res;
							})
						}
						onClose={() => setModalAssignment(null)}
					/>
				)}
			</Paper>
		</Grid>
	);
};
